import { BaseDataProvider } from "./baseProvider";
import type { UserListParams } from "../types/api";

export class UserDataProvider extends BaseDataProvider {
  constructor() {
    super("users");
  }

  async getList(_resource: string, params: UserListParams) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 20 };
    const { field, order } = params.sort || {
      field: "createdAt",
      order: "DESC",
    };
    const filter = params.filter || {};

    // Build query parameters for users API
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
      ...(field && { sortBy: field }),
      ...(order && { sortOrder: order.toUpperCase() }),
      ...(filter.search && { search: filter.search }),
      ...(filter.role && { role: filter.role }),
      ...(filter.onboardingCompleted !== undefined && {
        onboardingCompleted: filter.onboardingCompleted.toString(),
      }),
    });

    const json = await this.makeRequest(`users?${queryParams}`);

    // Handle different possible API response structures
    const apiResponse = json as {
      data?: unknown[];
      users?: unknown[];
      total?: number;
      totalCount?: number;
      pageInfo?: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    };

    // Use users array if data array is not available
    const dataArray = apiResponse.data || apiResponse.users || [];
    const total = apiResponse.total || apiResponse.totalCount || 0;

    return {
      data: dataArray,
      total: total,
      pageInfo: {
        hasNextPage: apiResponse.pageInfo?.hasNextPage || false,
        hasPreviousPage: apiResponse.pageInfo?.hasPreviousPage || false,
      },
    };
  }

  async getOne(_resource: string, params: Record<string, unknown>) {
    const endpoint = `users/${params.id}`;
    const response = await this.makeRequest(endpoint);
    return { data: response };
  }

  async getMany(resource: string, params: Record<string, unknown>) {
    // For getMany, we'll fetch each user individually
    const promises = (params.ids as string[]).map((id) =>
      this.getOne(resource, { id }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async getManyReference() {
    // This would typically be used for related resources
    return {
      data: [],
      total: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  // Users support create, update, and delete operations
  async create(_resource: string, params: Record<string, unknown>) {
    const endpoint = "users";
    const response = await this.makeRequest(endpoint, {
      method: "POST",
      body: JSON.stringify(params.data),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return { data: response };
  }

  async update(_resource: string, params: Record<string, unknown>) {
    const endpoint = `users/${params.id}`;
    const response = await this.makeRequest(endpoint, {
      method: "PUT",
      body: JSON.stringify(params.data),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return { data: response };
  }

  async updateMany(_resource: string, params: Record<string, unknown>) {
    // For bulk updates, we'll update each user individually
    const promises = (params.ids as string[]).map((id) =>
      this.update(_resource, { id, data: params.data }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async delete(_resource: string, params: Record<string, unknown>) {
    const endpoint = `users/${params.id}`;
    await this.makeRequest(endpoint, {
      method: "DELETE",
    });
    return { data: params.id };
  }

  async deleteMany(_resource: string, params: Record<string, unknown>) {
    // For bulk deletes, we'll delete each user individually
    const promises = (params.ids as string[]).map((id) =>
      this.delete(_resource, { id }),
    );
    await Promise.all(promises);
    return { data: params.ids };
  }
}
